<template>
  <div 
    class="tw-px-3 tw-py-2.5 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
    @click="openNotificationDetails"
  >
    <div class="tw-flex tw-items-center tw-gap-2">
      <!-- Dot for unread -->
      <div v-if="notification.unread" class="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-indigo-500"></div>
      <div v-else class="tw-w-1.5"></div>
      
      <!-- Meeting Icon -->
      <div class="tw-w-7 tw-h-7 tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100">
        <icon icon="solar:calendar-add-bold" class="tw-text-indigo-600 tw-text-sm" />
      </div>
      
      <!-- Content -->
      <div class="tw-flex-1 tw-min-w-0">
        <div class="tw-flex tw-items-start tw-justify-between">
          <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ notification.title }}</p>
          <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-ml-1">{{ formatTime(notification.created_at || notification.time) }}</span>
        </div>
        <p class="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-line-clamp-1">{{ notification.message }}</p>
        
        <!-- Simple meeting time if available -->
        <div v-if="meetingData?.start_time" class="tw-text-xs tw-text-indigo-600 tw-mt-1">
          {{ formatMeetingDateTime(meetingData.start_time) }}
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Details Popup -->
  <MeetingDetailsPopup 
    :visible="showMeetingPopup"
    :notification="notification"
    @close="closeMeetingPopup"
    @meeting-accepted="handleMeetingAccepted"
    @meeting-declined="handleMeetingDeclined"
    @meeting-joined="handleMeetingJoined"
  />
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue';
import { useI18n } from "vue-i18n";
import MeetingDetailsPopup from '../modals/MeetingDetailsPopup.vue';

const { t } = useI18n();

// Define props
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

// Define emits
const emit = defineEmits(['notificationRead', 'popupStateChanged']);

// State for popup
const showMeetingPopup = ref(false);

// Extract meeting data from notification
const meetingData = computed(() => {
  return props.notification?.data?.meeting || null;
});

// Open notification details and mark as read
function openNotificationDetails() {
  console.log(`MeetingNotification: openNotificationDetails called for notification ${props.notification.id}`);
  // Open the popup
  showMeetingPopup.value = true;
  emit('popupStateChanged', true);
  
  // Mark as read
  if (props.notification.unread) {
    emit('notificationRead', props.notification.id);
  }
}

// Close meeting popup
function closeMeetingPopup() {
  showMeetingPopup.value = false;
  emit('popupStateChanged', false);
}

// Handle popup events
function handleMeetingAccepted(meeting) {
  console.log('Meeting accepted from popup:', meeting);
  // Add API call here
}

function handleMeetingDeclined(meeting) {
  console.log('Meeting declined from popup:', meeting);
  // Add API call here
}

function handleMeetingJoined(meeting) {
  console.log('Meeting joined from popup:', meeting);
  // Add analytics or other tracking here
}

// Format meeting date and time for simple display
function formatMeetingDateTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let dateText;
    if (date.toDateString() === today.toDateString()) {
      dateText = t('common.today', 'Today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateText = t('common.tomorrow', 'Tomorrow');
    } else {
      // Check if the date string has a timezone indicator (Z suffix)
      const isUTC = typeof dateString === 'string' && dateString.includes('Z');
      
      if (isUTC) {
        // For UTC dates, use UTC methods to avoid timezone conversion
        const month = date.toLocaleString(undefined, { month: 'short' });
        const day = date.getUTCDate();
        dateText = `${month} ${day}`;
      } else {
        // For local dates, use local methods
        dateText = date.toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    }
    
    let timeText;
    // Check if the date string has a timezone indicator (Z suffix)
    const isUTC = typeof dateString === 'string' && dateString.includes('Z');
    
    if (isUTC) {
      // For UTC dates, use UTC methods to avoid timezone conversion
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      timeText = `${displayHours}:${displayMinutes} ${ampm}`;
    } else {
      // For local dates, use local methods
      timeText = date.toLocaleTimeString(undefined, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
    }
    
    return `${dateText} at ${timeText}`;
  } catch (error) {
    console.error('Error formatting meeting date time:', error);
    return '';
  }
}

// Format timestamp to relative time (e.g. "2h ago")
const formatTime = (timestamp) => {
  const now = new Date();
  let date;
  
  // Handle different timestamp formats
  if (typeof timestamp === 'string') {
    // ISO string format from API: "2025-04-26T23:35:51.000000Z"
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    // Unix timestamp (seconds)
    date = new Date(timestamp * 1000);
  } else if (timestamp instanceof Date) {
    // Already a Date object
    date = timestamp;
  } else if (timestamp?.data?.timestamp) {
    // Handle notification.data.timestamp format
    date = new Date(timestamp.data.timestamp * 1000);
  } else {
    // Fallback to created_at if timestamp is undefined
    date = timestamp?.created_at ? new Date(timestamp.created_at) : new Date();
  }
  
  // Invalid date, use current time
  if (isNaN(date.getTime())) {
    date = new Date();
  }
  
  const diff = now - date;
  
  // Convert to minutes
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 60) {
    return minutes <= 0 
      ? t('time.just-now', 'Just now')
      : t('time.min-ago', { count: minutes }, `${minutes}m`);
  }
  
  // Convert to hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('time.hour-ago', { count: hours }, `${hours}h`);
  }
  
  // Convert to days
  const days = Math.floor(hours / 24);
  return t('time.day-ago', { count: days }, `${days}d`);
};
</script>
